/**
 * @fileoverview Главный сервис для работы с mention
 * @description Централизованная логика создания, обновления и управления mention узлами
 */

import {
  generateDisplayKey,
  parseDisplayKey,
  formatDisplayKey,
  // createMentionUID, // Экспортировано для будущего использования
} from "./keyGenerator";
import {
  createFieldsMap,
  findFieldValue,
  // doesMentionMatchField, // Экспортировано для будущего использования
  filterFieldsByParticipant,
  groupFieldsByService,
} from "./fieldMatcher";
import { MENTION_TYPES, SPECIAL_SERVICE_CODES } from "./constants";

/**
 * Сервис для работы с mention узлами
 */
class MentionService {
  /**
   * Создает payload для вставки mention в редактор
   * @param {Object} options - Опции mention
   * @param {string} options.key - Ключ поля
   * @param {string} options.label - Читаемое название
   * @param {string} [options.service_code] - Код сервиса
   * @param {string} [options.participant_type_code] - Код участника
   * @param {boolean} [options.isCustom] - Кастомное поле
   * @param {boolean} [options.isNotary] - Поле нотариуса
   * @param {string} [options.value] - Начальное значение
   * @returns {import('./types').MentionAttributes} Атрибуты mention
   */
  createMentionPayload({
    key,
    label,
    service_code,
    participant_type_code,
    isCustom = false,
    isNotary = false,
    value = "",
  }) {
    if (!key) {
      throw new Error("MentionService: key is required");
    }

    // Для кастомных полей и нотариуса displayKey = key
    let displayKey;
    if (isCustom || isNotary) {
      displayKey = key;
    } else if (service_code) {
      displayKey = generateDisplayKey(service_code, key, participant_type_code);
    } else {
      displayKey = key;
    }

    return {
      key,
      displayKey,
      label: label || key,
      service_code: service_code || null,
      participant_type_code: participant_type_code || null,
      value: value || "",
      isCustom,
      isNotary,
    };
  }

  /**
   * Преобразует поле из сервиса в формат для обновления mention
   * @param {import('./types').ServiceField} field - Поле из сервиса
   * @returns {import('./types').MentionAttributes} Атрибуты для обновления
   */
  fieldToMentionUpdate(field) {
    if (!field || !field.key) {
      console.warn("MentionService.fieldToMentionUpdate: invalid field", field);
      return null;
    }

    const displayKey = generateDisplayKey(
      field.service_code,
      field.key,
      field.participant_type_code
    );

    return {
      key: field.key,
      displayKey,
      value: field.value,
      service_code: field.service_code,
      participant_type_code: field.participant_type_code,
      label: field.display_name || field.key,
    };
  }

  /**
   * Подготавливает массив полей для bulk обновления mention
   * @param {import('./types').ServiceField[]} fields - Массив полей
   * @returns {Map} Карта полей для быстрого поиска
   */
  prepareFieldsForBulkUpdate(fields) {
    return createFieldsMap(fields);
  }

  /**
   * Находит соответствие mention узла в карте полей
   * @param {import('./types').MentionAttributes} mentionAttrs - Атрибуты mention
   * @param {Map} fieldsMap - Карта полей
   * @returns {any|null} Найденное значение
   */
  findMatchingFieldValue(mentionAttrs, fieldsMap) {
    return findFieldValue(
      fieldsMap,
      mentionAttrs.service_code,
      mentionAttrs.key,
      mentionAttrs.participant_type_code
    );
  }

  /**
   * Проверяет, требует ли mention выбора участника
   * @param {string} service_code - Код сервиса
   * @param {boolean} isCustom - Кастомное поле
   * @param {boolean} isNotary - Поле нотариуса
   * @returns {boolean} true, если нужен выбор участника
   */
  requiresParticipantSelection(service_code, isCustom, isNotary) {
    if (isCustom || isNotary) {
      return false;
    }

    // Специальные сервисы не требуют выбора участника
    if (Object.values(SPECIAL_SERVICE_CODES).includes(service_code)) {
      return false;
    }

    return true;
  }

  /**
   * Определяет тип mention по его атрибутам
   * @param {import('./types').MentionAttributes} attrs - Атрибуты mention
   * @returns {string} Тип mention
   */
  getMentionType(attrs) {
    if (attrs.isNotary) return MENTION_TYPES.NOTARY;
    if (attrs.isCustom) return MENTION_TYPES.CUSTOM;
    return MENTION_TYPES.SERVICE;
  }

  /**
   * Валидирует атрибуты mention
   * @param {import('./types').MentionAttributes} attrs - Атрибуты для проверки
   * @returns {{valid: boolean, errors: string[]}} Результат валидации
   */
  validateMentionAttributes(attrs) {
    const errors = [];

    if (!attrs) {
      errors.push("Mention attributes are required");
      return { valid: false, errors };
    }

    if (!attrs.key) {
      errors.push("key is required");
    }

    if (!attrs.displayKey) {
      errors.push("displayKey is required");
    }

    // Для обычных сервисов требуется service_code
    if (!attrs.isCustom && !attrs.isNotary && !attrs.service_code) {
      errors.push("service_code is required for service mentions");
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Парсит displayKey и возвращает компоненты
   * @param {string} displayKey - Полный ключ mention
   * @returns {import('./types').MentionKeyComponents} Компоненты ключа
   */
  parseDisplayKey(displayKey) {
    return parseDisplayKey(displayKey);
  }

  /**
   * Генерирует displayKey из компонентов
   * @param {string} service_code - Код сервиса
   * @param {string} key - Ключ поля
   * @param {string} [participant_type_code] - Код участника
   * @returns {string} Сформированный displayKey
   */
  generateDisplayKey(service_code, key, participant_type_code) {
    return generateDisplayKey(service_code, key, participant_type_code);
  }

  /**
   * Форматирует ключ для отображения с префиксом $
   * @param {string} key - Ключ mention
   * @returns {string} Отформатированный ключ
   */
  formatDisplayKey(key) {
    return formatDisplayKey(key);
  }

  /**
   * Фильтрует поля по участнику
   * @param {import('./types').ServiceField[]} fields - Поля
   * @param {string} participant_type_code - Код участника
   * @returns {import('./types').ServiceField[]} Отфильтрованные поля
   */
  filterFieldsByParticipant(fields, participant_type_code) {
    return filterFieldsByParticipant(fields, participant_type_code);
  }

  /**
   * Группирует поля по сервисам
   * @param {import('./types').ServiceField[]} fields - Поля
   * @returns {Object.<string, import('./types').ServiceField[]>} Сгруппированные поля
   */
  groupFieldsByService(fields) {
    return groupFieldsByService(fields);
  }
}

// Экспортируем singleton instance
export default new MentionService();
